import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'

import type {
    StocktakeKind,
    StocktakeSessionTime,
} from '@/lib/api/stocktake-api'
import type { SnapshotKind } from '@/lib/api/inventory-stats-api'
import { snapshotBatchApi } from '@/lib/api/inventory-stats-api'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    useCreateStocktake,
    useStocktakeSessions,
} from '@/hooks/use-stocktake-query'
import { formatDateVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar/stocktake/new')({
    component: Page,
})

const KIND_OPTIONS: { value: StocktakeKind; label: () => string }[] = [
    { value: 'purchase', label: () => m.stocktake_kind_purchase() },
    { value: 'sorting', label: () => m.stocktake_kind_sorting() },
    { value: 'scenting', label: () => m.stocktake_kind_scenting() },
    { value: 'packaging', label: () => m.stocktake_kind_packaging() },
    { value: 'product', label: () => m.stocktake_kind_product() },
]

function Page() {
    const navigate = useNavigate()
    const create = useCreateStocktake()

    const [kind, setKind] = useState<StocktakeKind | ''>('')
    // Default theo giờ: trước 12h → morning (kiểm sáng T+1), sau 12h → evening.
    const [sessionTime, setSessionTime] = useState<StocktakeSessionTime>(() =>
        new Date().getHours() < 12 ? 'morning' : 'evening',
    )
    const [snapshotBatchId, setSnapshotBatchId] = useState('')
    const [notes, setNotes] = useState('')

    // Reset snapshot chọn khi đổi kind hoặc chuyển sang morning (morning tự
    // chụp — không cần snapshot picker).
    useEffect(() => {
        setSnapshotBatchId('')
    }, [kind, sessionTime])

    const { data: batches, isLoading } = useQuery({
        queryKey: ['snapshot-batches', kind, 'approved'],
        enabled: !!kind,
        queryFn: async () => {
            const res = await snapshotBatchApi.list({
                kind: kind as SnapshotKind,
            })
            const items = res.data ?? []
            return items
                .filter((b) => b.status === 'approved')
                .sort((a, b) => b.snapshot_date.localeCompare(a.snapshot_date))
        },
    })

    // Load ALL sessions with kind = current + status ∈ {approved, pending_approval,
    // counting, draft} — bất kể ngày kiểm nào cũng chặn tạo trùng ngày.
    // Sau đó filter batches: nếu batch.snapshot_date đã có session tương ứng thì
    // loại khỏi dropdown để tránh sinh trùng PNK/PXK khi duyệt.
    const { data: sessions } = useStocktakeSessions(
        kind ? { kind, per_page: 200 } : undefined,
    )
    const todayISO = new Date().toISOString().slice(0, 10)
    const takenDates = useMemo(() => {
        const set = new Set<string>()
        for (const s of sessions?.items ?? []) {
            if (s.status !== 'cancelled') {
                // Bỏ qua session đã hủy; tất cả trạng thái còn lại đều chiếm chỗ.
                set.add(s.session_date.slice(0, 10))
            }
        }
        return set
    }, [sessions])

    const batchOptions = useMemo(
        () =>
            (batches ?? []).filter(
                (b) => !takenDates.has(b.snapshot_date.slice(0, 10)),
            ),
        [batches, takenDates],
    )

    // Message priority: kind not picked → nothing; đã kiểm hôm nay → warning riêng;
    // không có chốt sổ approved → warning gốc.
    const hasAnyBatch = (batches ?? []).length > 0
    const alreadyCountedToday = takenDates.has(todayISO)
    const selectedBatch = useMemo(
        () => batchOptions.find((b) => b.id === snapshotBatchId),
        [batchOptions, snapshotBatchId],
    )

    // Cycle date T (hiển thị cho user biết snapshot morning auto sẽ chụp cho
    // ngày nào — ngày hôm qua so với hôm nay).
    const cycleDateT = useMemo(() => {
        const d = new Date()
        d.setDate(d.getDate() - 1)
        return formatDateVN(d.toISOString().slice(0, 10))
    }, [])

    const isMorning = sessionTime === 'morning'
    const canSubmit =
        !!kind && !create.isPending && (isMorning || !!snapshotBatchId)

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!kind) return
        if (!isMorning && !snapshotBatchId) return
        create.mutate(
            {
                kind,
                session_time: sessionTime,
                // Chỉ gửi snapshot_batch_id khi evening. Morning để BE tự tạo.
                snapshot_batch_id: isMorning ? null : snapshotBatchId,
                notes,
            },
            {
                onSuccess: (res) => {
                    if (res.data?.id) {
                        void navigate({
                            to: '/stocktake/$id',
                            params: { id: res.data.id },
                        })
                    }
                },
            },
        )
    }

    return (
        <div className="max-w-3xl mx-auto space-y-4 p-4 pb-24 md:p-6 md:pb-6">
            <Button asChild variant="ghost" size="sm">
                <a onClick={() => void navigate({ to: '/stocktake' })}>
                    <ArrowLeft className="size-4 mr-1" />
                    {m.stocktake_action_back()}
                </a>
            </Button>

            <Card>
                <CardHeader>
                    <CardTitle>{m.stocktake_new_title()}</CardTitle>
                </CardHeader>
                <CardContent>
                    <form
                        onSubmit={handleSubmit}
                        className="flex flex-col gap-4"
                    >
                        <div className="grid gap-2">
                            <Label>{m.stocktake_field_kind()} *</Label>
                            <Select
                                value={kind}
                                onValueChange={(v) =>
                                    setKind(v as StocktakeKind)
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue
                                        placeholder={m.stocktake_wizard_choose_kind_placeholder()}
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {KIND_OPTIONS.map((opt) => (
                                        <SelectItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label()}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid gap-2">
                            <Label>{m.stocktake_field_session_time()} *</Label>
                            <Select
                                value={sessionTime}
                                onValueChange={(v) =>
                                    setSessionTime(v as StocktakeSessionTime)
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="evening">
                                        {m.stocktake_session_time_evening()}
                                    </SelectItem>
                                    <SelectItem value="morning">
                                        {m.stocktake_session_time_morning()}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {isMorning ? (
                            <div className="rounded-md border bg-emerald-50/60 border-emerald-200 p-3 text-sm text-emerald-900">
                                {m.stocktake_morning_help({
                                    date: cycleDateT,
                                })}
                            </div>
                        ) : (
                            <div className="grid gap-2">
                                <Label>{m.stocktake_field_snapshot()} *</Label>
                                {!kind ? (
                                    <p className="text-sm text-muted-foreground">
                                        {m.stocktake_wizard_choose_kind_first()}
                                    </p>
                                ) : isLoading ? (
                                    <p className="text-sm text-muted-foreground">
                                        {m.common_loading()}
                                    </p>
                                ) : alreadyCountedToday ? (
                                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                                        {m.stocktake_already_counted_today()}
                                    </p>
                                ) : batchOptions.length === 0 ? (
                                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                                        {hasAnyBatch
                                            ? m.stocktake_all_snapshots_taken()
                                            : m.stocktake_no_snapshot_hint()}
                                    </p>
                                ) : (
                                    <Select
                                        value={snapshotBatchId}
                                        onValueChange={setSnapshotBatchId}
                                    >
                                        <SelectTrigger>
                                            <SelectValue
                                                placeholder={m.stocktake_wizard_choose_snapshot_placeholder()}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {batchOptions.map((b) => (
                                                <SelectItem
                                                    key={b.id}
                                                    value={b.id}
                                                >
                                                    {m.stocktake_snapshot_batch_summary(
                                                        {
                                                            date: formatDateVN(
                                                                b.snapshot_date,
                                                            ),
                                                            count: String(
                                                                b.total_lots,
                                                            ),
                                                        },
                                                    )}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>
                        )}

                        {selectedBatch && (
                            <div className="rounded-md border bg-muted/40 p-3 text-sm">
                                <p>
                                    <strong>
                                        {formatDateVN(
                                            selectedBatch.snapshot_date,
                                        )}
                                    </strong>
                                </p>
                                <p className="text-muted-foreground">
                                    {m.stocktake_snapshot_batch_qty({
                                        count: String(selectedBatch.total_lots),
                                        qty: String(
                                            selectedBatch.total_quantity,
                                        ),
                                    })}
                                </p>
                            </div>
                        )}

                        <div className="grid gap-2">
                            <Label>{m.stocktake_field_notes()}</Label>
                            <Input
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder={m.stocktake_notes_placeholder_optional()}
                            />
                        </div>

                        <StickyFormFooter>
                            <Button
                                type="button"
                                variant="outline"
                                className="h-11 flex-1 md:h-10 md:flex-none"
                                onClick={() =>
                                    void navigate({ to: '/stocktake' })
                                }
                            >
                                {m.common_cancel()}
                            </Button>
                            <Button
                                type="submit"
                                className="h-11 flex-1 md:h-10 md:flex-none"
                                disabled={!canSubmit}
                            >
                                {m.stocktake_action_create()}
                            </Button>
                        </StickyFormFooter>
                    </form>
                </CardContent>
            </Card>
        </div>
    )
}
